export type MembershipDiscountSource = 'base' | 'special' | 'period' | 'none';

export interface MembershipPeriodOffer {
  discountPercent: number;
  startDate: Date | string;
  endDate: Date | string;
}

export interface SpecialOfferProductConfig {
  discountPercent?: number;
  periodOffers?: MembershipPeriodOffer[];
}

export interface MembershipDiscountInput {
  _id?: unknown;
  alias?: string;
  name?: string;
  discountPercent?: number;
  qualified?: boolean;
  benefits?: {
    allProducts?: boolean;
    specialOfferProducts?: { [productId: string]: SpecialOfferProductConfig };
  };
}

export interface MembershipDiscountResolution {
  percent: number;
  source: MembershipDiscountSource;
  basePercent: number;
  specialPercent?: number;
  period?: MembershipPeriodOffer;
}

export function isPeriodOfferActive(
  offer: MembershipPeriodOffer,
  now: Date = new Date(),
): boolean {
  const start = offer.startDate instanceof Date ? offer.startDate : new Date(offer.startDate);
  const end = offer.endDate instanceof Date ? offer.endDate : new Date(offer.endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return false;
  }
  return start.getTime() <= now.getTime() && now.getTime() < end.getTime();
}

export function resolveMembershipDiscount(
  membership: MembershipDiscountInput | undefined | null,
  productId?: string,
  now: Date = new Date(),
): MembershipDiscountResolution {
  const basePercent = membership?.discountPercent || 0;
  const special = productId ? membership?.benefits?.specialOfferProducts?.[productId] : undefined;
  const specialPercent =
    special?.discountPercent != null && special.discountPercent > 0 ? special.discountPercent : undefined;

  const activePeriods = (special?.periodOffers || []).filter((offer) => isPeriodOfferActive(offer, now));
  if (activePeriods.length > 0) {
    const period = activePeriods.reduce((best, current) =>
      (current.discountPercent || 0) > (best.discountPercent || 0) ? current : best,
    );
    return {
      percent: period.discountPercent || 0,
      source: 'period',
      basePercent,
      specialPercent,
      period,
    };
  }

  if (specialPercent != null) {
    return {
      percent: specialPercent,
      source: 'special',
      basePercent,
      specialPercent,
    };
  }

  if (basePercent > 0) {
    return {
      percent: basePercent,
      source: 'base',
      basePercent,
    };
  }

  return {
    percent: 0,
    source: 'none',
    basePercent,
  };
}
